import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { CompareShortTermRentalRate, Day } from 'lumen/models';

interface FormatOptions {
  includeCurrency: boolean;
}

export default class ShortTermRentalRate extends Model {
  [Type] = 'short-term-rental-rate' as const;

  @belongsTo('day', { async: false, inverse: 'rentalRates' })
  declare day: Day;

  @attr('string') declare currency: string;
  @attr('dayjs') declare extractDate: NonNullable<DayjsTransformReturn>;
  @attr('string') declare message: string;
  @attr('number') declare value: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  modelName = 'short-term-rental-rate';

  // Don't show changes smaller than 0.5 (will be rounded to 0)
  SIGNIFICANT_DIFFERENCE = 0.5;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDate() {
    return this.extractDate.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    if (this.isMissing) {
      return this.intl.t('general.missing');
    }
    return this.hotelAndMarketService.formatRate(this.value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  compareObjectForDay(referenceChangeDays: number) {
    const referenceExtractDate = this.hotelAndMarketService
      .getLocalTime()
      .subtract(referenceChangeDays, 'day')
      .format('YYYY-MM-DD');
    return this.compareObjectForExtractDate(referenceExtractDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  compareObjectForExtractDate(referenceExtractDate: string) {
    return this.day.compareShortTermRentalRates?.find(
      (item) => item.extractDate.format('YYYY-MM-DD') === referenceExtractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  differenceForCompareObject(compareObject?: CompareShortTermRentalRate) {
    // CompareObject is undefined when switching between compare period (e.g. vs 7 days ago -> vs. 14 days ago)
    if (
      this.isMissing ||
      compareObject === undefined ||
      compareObject.isMissing
    ) {
      return 0;
    }

    return this.value - compareObject.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  differenceForDay(referenceChangeDays: number) {
    const compareObject = this.compareObjectForDay(referenceChangeDays);
    return this.differenceForCompareObject(compareObject);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  differenceForExtractDate(referenceExtractDate: string) {
    const compareObject =
      this.compareObjectForExtractDate(referenceExtractDate);
    return this.differenceForCompareObject(compareObject);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formattedDifferenceForCompareObject(
    compareObject: CompareShortTermRentalRate | undefined,
    options: FormatOptions,
  ) {
    const difference = this.differenceForCompareObject(compareObject);
    if (
      this.isMissing ||
      Math.abs(difference) < this.SIGNIFICANT_DIFFERENCE ||
      compareObject === undefined
    ) {
      return '';
    }

    if (compareObject.isMissing) {
      return this.intl.t('general.missing');
    }

    return this.hotelAndMarketService.formatRate(difference, {
      includeCurrencySymbol: options.includeCurrency,
      includeSign: true,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formattedDifferenceForDay(
    referenceChangeDays: number,
    options = { includeCurrency: true },
  ) {
    const compareObject = this.compareObjectForDay(referenceChangeDays);
    return this.formattedDifferenceForCompareObject(compareObject, options);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formattedDifferenceForExtractDate(
    referenceExtractDate: string,
    options: FormatOptions = { includeCurrency: true },
  ) {
    const compareObject =
      this.compareObjectForExtractDate(referenceExtractDate);
    return this.formattedDifferenceForCompareObject(compareObject, options);
  }
}
